import { useCallback, useEffect, useState } from 'react';
import { useClinicalEditors } from './context';
import type { PublicInvestigatorDetail } from '@rudi/shared/types/clinical-trials';

interface UsePublicInvestigatorResult {
  investigator: PublicInvestigatorDetail | null;
  loading: boolean;
  error: string | null;
  refresh: () => void;
}

export function usePublicInvestigator(slug?: string | null): UsePublicInvestigatorResult {
  const { getPublicInvestigator } = useClinicalEditors();
  const [investigator, setInvestigator] = useState<PublicInvestigatorDetail | null>(null);
  const [loading, setLoading] = useState<boolean>(Boolean(slug));
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(() => {
    if (!slug) {
      setInvestigator(null);
      setLoading(false);
      setError(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const data = await getPublicInvestigator(slug);
        if (!cancelled) setInvestigator(data);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : String(err));
          setInvestigator(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [slug, getPublicInvestigator]);

  useEffect(() => {
    return refresh();
  }, [refresh]);

  return {
    investigator: investigator?.slug === slug ? investigator : null,
    loading,
    error,
    refresh,
  };
}
